import dayjs from 'dayjs';
import type {
  AnalyticsDateRange,
  AnalyticsKpiDashboardPayload,
  AnalyticsKpiMetric,
  FounderTarget,
} from '../../services/analytics';

/**
 * The founder summary: six plain-language cards, the couple journey and a
 * short "what changed" list, all read from the KPI payload Connect already
 * returns. Percentages are fractions (0.58 is 58%), as in the payload.
 */

export type FounderCardKind = 'count' | 'percent';
export type FounderStatus = 'good' | 'watch' | 'bad';

export interface FounderCardDefinition {
  id: string;
  question: string;
  title: string;
  description: string;
  kind: FounderCardKind;
  howMeasured: {
    counts: string;
    doesntCount: string;
    whyItMatters: string;
    source: string;
  };
  /** Shown instead of a number when the value can't be measured yet. */
  unavailableNote: string;
}

export const FOUNDER_CARDS: FounderCardDefinition[] = [
  {
    id: 'weekly_active_connected_couples',
    question: 'Is Ourora alive?',
    title: 'Weekly active couples',
    description: 'Couples where both partners messaged, called or shared something in the last 7 days. Each couple counted once.',
    kind: 'count',
    howMeasured: {
      counts: 'A couple, once, if both partners did at least one of: sent a message, voice note or media, started a call, added a memory, or used Echos, in the last 7 complete days.',
      doesntCount: 'Only opening the app, receiving notifications, test accounts. Couples where a partner turned analytics off can’t be counted, so this slightly undercounts.',
      whyItMatters: 'It’s the clearest sign Ourora is part of a couple’s week. If it grows, the product is working.',
      source: 'Daily couple activity · complete days only',
    },
    unavailableNote: 'No couple activity has been recorded for this period yet.',
  },
  {
    id: 'invite_to_pair_rate',
    question: 'Do partners actually join?',
    title: 'Pair rate',
    description: 'Of people who sent their first invite, how many got their partner into Ourora.',
    kind: 'percent',
    howMeasured: {
      counts: 'People who sent their first invite in this period and are now paired, out of everyone who sent a first invite.',
      doesntCount: 'Invites sent before this was tracked. Abandoned invites stay in, so this isn’t flattered.',
      whyItMatters: 'Ourora only works for two. Every unpaired sign-up is a lost couple.',
      source: 'First invite sent and pairing records on the server',
    },
    unavailableNote: 'No one sent a first invite in this period.',
  },
  {
    id: 'first_shared_moment_rate',
    question: 'Do they feel it fast?',
    title: 'First shared moment',
    description: 'Of couples who paired in this period, how many have both messaged, called or shared something together.',
    kind: 'percent',
    howMeasured: {
      counts: 'Couples who paired in this period where both partners messaged, called, shared a memory or used Echos.',
      doesntCount: 'Couples who paired before this period. Couples where a partner turned analytics off, so this slightly undercounts.',
      whyItMatters: 'Couples who share something early are the ones who stay.',
      source: 'Couple journey · day-level, no extra tracking',
    },
    unavailableNote: 'No couples paired in this period.',
  },
  {
    id: 'week_4_couple_retention',
    question: 'Do they come back?',
    title: 'Week-4 couple retention',
    description: 'Of couples who paired about 4 weeks ago, how many were still active together in their 4th week.',
    kind: 'percent',
    howMeasured: {
      counts: 'Couples who paired 28 to 34 days before the last complete day and did something meaningful together in their 4th week (days 21 to 27).',
      doesntCount: 'Couples who paired more recently or earlier than that window.',
      whyItMatters: 'Week 4 shows whether Ourora became a habit or a novelty.',
      source: 'Couple journey · daily couple activity',
    },
    unavailableNote: 'No couples paired in the 4-weeks-ago window.',
  },
  {
    id: 'current_paid_couples',
    question: 'Will it make money?',
    title: 'Paid couples',
    description: 'Couples paying for Plus right now. Free grants and test accounts not counted.',
    kind: 'count',
    howMeasured: {
      counts: 'Couples with an active Plus subscription bought through the App Store or Google Play, right now.',
      doesntCount: 'Admin-granted Plus, expired subscriptions and test accounts.',
      whyItMatters: 'It’s the business. Growth without paying couples doesn’t last.',
      source: 'Store-verified subscriptions on the server',
    },
    unavailableNote: 'Subscription data isn’t available.',
  },
  {
    id: 'crash_free_sessions',
    question: 'Is it breaking?',
    title: 'Crash-free sessions',
    description: 'Share of app sessions that didn’t crash.',
    kind: 'percent',
    howMeasured: {
      counts: 'App sessions without a crash or fatal error, out of all sessions.',
      doesntCount: 'Crashes on app versions without crash reporting (before the D-05 app update), and sessions from people who turned analytics off.',
      whyItMatters: 'A private app has to feel solid. Crashes break trust fast.',
      source: 'Crash reports from the app · daily sessions',
    },
    unavailableNote: 'No sessions with crash reporting yet. Needs the app update with crash reporting (D-05).',
  },
];

export interface FounderCardData {
  definition: FounderCardDefinition;
  value: number | null;
  previous: number | null;
  /** Daily values for the sparkline, when the payload has a daily series. */
  trend: number[] | null;
  /** Extra context under the number, e.g. "7.9% of active couples". */
  context: string | null;
  target: FounderTarget | null;
  status: FounderStatus | null;
}

function metricById(data: AnalyticsKpiDashboardPayload | undefined, id: string): AnalyticsKpiMetric | null {
  for (const group of data?.groups || []) {
    const metric = group.metrics.find((item) => item.id === id);
    if (metric) return metric;
  }
  return null;
}

function readyValue(metric: AnalyticsKpiMetric | null): number | null {
  if (!metric || metric.status !== 'ready' || metric.value === null || !Number.isFinite(metric.value)) return null;
  return metric.value;
}

function journeyRate(data: AnalyticsKpiDashboardPayload | undefined, stepId: string): number | null {
  const step = data?.journey?.find((item) => item.id === stepId);
  return step && step.rateOfPrevious !== null && Number.isFinite(step.rateOfPrevious) ? step.rateOfPrevious : null;
}

/** The card's number in a payload, or null when it can't be measured. */
export function founderValue(data: AnalyticsKpiDashboardPayload | undefined, id: string): number | null {
  switch (id) {
    case 'first_shared_moment_rate':
      return journeyRate(data, 'first_shared_moment');
    case 'week_4_couple_retention':
      return journeyRate(data, 'week_4');
    case 'crash_free_sessions': {
      const metric = metricById(data, id);
      return metric && (metric.denominator ?? 0) > 0 ? readyValue(metric) : null;
    }
    default:
      return readyValue(metricById(data, id));
  }
}

function founderTrend(data: AnalyticsKpiDashboardPayload, id: string): number[] | null {
  const series = data.series || [];
  if (series.length < 2) return null;
  if (id === 'weekly_active_connected_couples') {
    return series.map((point) => point.weeklyActiveConnectedCouples);
  }
  if (id === 'crash_free_sessions') {
    const points = series.filter((point) => point.sessions > 0);
    return points.length >= 2 ? points.map((point) => 1 - point.fatalErrors / point.sessions) : null;
  }
  return null;
}

/**
 * Good at or above the target, Watch within 10% below it, Bad further below.
 * No target or no value: no status.
 */
export function founderStatus(value: number | null, target: number | null | undefined): FounderStatus | null {
  if (value === null || target === null || target === undefined || !Number.isFinite(target)) return null;
  if (value >= target) return 'good';
  if (value >= target * 0.9) return 'watch';
  return 'bad';
}

/** The values each status covers for a target, for the Set target dialog. */
export function statusBands(target: number, kind: FounderCardKind) {
  const watchFrom = target * 0.9;
  if (kind === 'count') {
    const good = Math.ceil(target);
    const watch = Math.ceil(watchFrom);
    return {
      good: `${formatFounderValue(good, kind)} or more`,
      watch: watch < good ? `${formatFounderValue(watch, kind)} – ${formatFounderValue(good - 1, kind)}` : '—',
      bad: `below ${formatFounderValue(watch, kind)}`,
    };
  }
  return {
    good: `${formatFounderValue(target, kind)} or more`,
    watch: `${formatFounderValue(watchFrom, kind)} – just under ${formatFounderValue(target, kind)}`,
    bad: `below ${formatFounderValue(watchFrom, kind)}`,
  };
}

export function formatFounderValue(value: number | null, kind: FounderCardKind): string {
  if (value === null || !Number.isFinite(value)) return '—';
  if (kind === 'percent') {
    const percent = value * 100;
    // Near 100% (crash-free) and small shares (paid couples) need a decimal
    // to mean anything.
    const digits = (percent >= 99 && percent < 100) || (percent > 0 && percent < 10) ? 1 : 0;
    return `${percent.toFixed(digits)}%`;
  }
  return Math.round(value).toLocaleString('en-US');
}

/** "+6% vs previous 4 weeks" for counts, "−3 pts vs previous 4 weeks" for rates. */
export function founderDelta(
  value: number | null,
  previous: number | null,
  kind: FounderCardKind,
  comparisonLabel: string,
): { text: string; direction: 'up' | 'down' | 'flat' } | null {
  if (value === null || previous === null) return null;
  if (kind === 'percent') {
    const points = Math.round((value - previous) * 100);
    const direction = points > 0 ? 'up' : points < 0 ? 'down' : 'flat';
    const sign = points > 0 ? '+' : points < 0 ? '−' : '±';
    return { text: `${sign}${Math.abs(points)} pts ${comparisonLabel}`, direction };
  }
  if (previous === 0) {
    if (value === 0) return { text: `no change ${comparisonLabel}`, direction: 'flat' };
    return { text: `+${formatFounderValue(value, kind)} ${comparisonLabel}`, direction: 'up' };
  }
  const percent = Math.round(((value - previous) / previous) * 100);
  const direction = percent > 0 ? 'up' : percent < 0 ? 'down' : 'flat';
  const sign = percent > 0 ? '+' : percent < 0 ? '−' : '±';
  return { text: `${sign}${Math.abs(percent)}% ${comparisonLabel}`, direction };
}

export function buildFounderCards(
  data: AnalyticsKpiDashboardPayload,
  previousData: AnalyticsKpiDashboardPayload | undefined,
  targets: FounderTarget[],
): FounderCardData[] {
  const targetById = new Map(targets.map((target) => [target.metricId, target]));
  return FOUNDER_CARDS.map((definition) => {
    const value = founderValue(data, definition.id);
    // Paid couples is "right now" whatever the period, so a comparison with
    // an earlier period would always read as no change.
    const previous = definition.id === 'current_paid_couples' ? null : founderValue(previousData, definition.id);
    const target = targetById.get(definition.id) || null;
    let context: string | null = null;
    if (definition.id === 'current_paid_couples' && value !== null) {
      const active = data.summary?.activeMeaningfulCouples || 0;
      context = active > 0 ? `${formatFounderValue(value / active, 'percent')} of active couples` : null;
    }
    return {
      definition,
      value,
      previous,
      trend: value === null ? null : founderTrend(data, definition.id),
      context,
      target,
      status: founderStatus(value, target?.target),
    };
  });
}

export type FounderChangeTone = 'up' | 'down' | 'good' | 'info';

export interface FounderChange {
  tone: FounderChangeTone;
  text: string;
}

/** A few plain sentences about what moved. Never more than four. */
export function buildFounderChanges(cards: FounderCardData[]): FounderChange[] {
  const changes: FounderChange[] = [];
  const card = (id: string) => cards.find((item) => item.definition.id === id);

  const active = card('weekly_active_connected_couples');
  if (active && active.value !== null && active.previous !== null && active.value !== active.previous) {
    const diff = Math.round(active.value - active.previous);
    changes.push(diff > 0
      ? { tone: 'up', text: `More couples active together: ${formatFounderValue(active.value, 'count')}, up ${diff} from the previous period.` }
      : { tone: 'down', text: `Fewer couples active together: ${formatFounderValue(active.value, 'count')}, down ${Math.abs(diff)} from the previous period.` });
  }

  const pair = card('invite_to_pair_rate');
  if (pair && pair.value !== null && pair.previous !== null) {
    const points = Math.round((pair.value - pair.previous) * 100);
    if (points !== 0) {
      changes.push(points > 0
        ? { tone: 'up', text: `More people are getting their partner in (${formatFounderValue(pair.value, 'percent')}).` }
        : { tone: 'down', text: `Fewer people are getting their partner in (${formatFounderValue(pair.value, 'percent')}).` });
    }
  }

  const onTarget = cards.filter((item) => item.status === 'good');
  if (onTarget.length > 0) {
    changes.push({
      tone: 'good',
      text: onTarget.length === 1
        ? `${onTarget[0].definition.title} is at or above target.`
        : `${onTarget.length} numbers are at or above target.`,
    });
  }
  const offTarget = cards.filter((item) => item.status === 'bad');
  if (offTarget.length > 0 && changes.length < 4) {
    changes.push({
      tone: 'down',
      text: offTarget.length === 1
        ? `${offTarget[0].definition.title} is well below target.`
        : `${offTarget.length} numbers are well below target.`,
    });
  }

  const missing = cards.filter((item) => item.value === null).length;
  if (missing > 0 && changes.length < 4) {
    changes.push({
      tone: 'info',
      text: missing === 1 ? '1 number can’t be measured yet.' : `${missing} numbers can’t be measured yet.`,
    });
  }
  return changes.slice(0, 4);
}

/** The same number of days just before this period, for comparisons. */
export function previousPeriodRange(data: AnalyticsKpiDashboardPayload | undefined): AnalyticsDateRange | null {
  const start = data?.period?.start;
  const days = data?.period?.days;
  if (!start || !days || days < 1) return null;
  const startDay = dayjs(start);
  if (!startDay.isValid()) return null;
  return {
    fromDate: startDay.subtract(days, 'day').format('YYYY-MM-DD'),
    toDate: startDay.subtract(1, 'day').format('YYYY-MM-DD'),
  };
}

/** "previous 4 weeks" or "previous 30 days". */
export function comparisonLabel(days: number | undefined): string {
  if (!days) return 'vs previous period';
  if (days % 7 === 0) {
    const weeks = days / 7;
    return weeks === 1 ? 'vs previous week' : `vs previous ${weeks} weeks`;
  }
  return `vs previous ${days} days`;
}

/** "Last 4 complete weeks · Sep 8 – Oct 5, 2026 · test accounts excluded". */
export function founderPeriodLabel(data: AnalyticsKpiDashboardPayload): string {
  const { start, end, days } = data.period;
  const span = days % 7 === 0
    ? `Last ${days / 7 === 1 ? 'complete week' : `${days / 7} complete weeks`}`
    : `Last ${days} complete days`;
  const from = dayjs(start);
  const to = dayjs(end);
  const range = from.year() === to.year()
    ? `${from.format('MMM D')} – ${to.format('MMM D, YYYY')}`
    : `${from.format('MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
  return `${span} · ${range} · test accounts excluded`;
}

/** Turns what someone typed into a stored target (62 → 0.62 for percent cards). */
export function targetFromInput(input: number | null, kind: FounderCardKind): number | null {
  if (input === null || !Number.isFinite(input) || input < 0) return null;
  if (kind === 'percent') return input > 100 ? null : input / 100;
  return Math.round(input);
}

export function targetToInput(target: number, kind: FounderCardKind): number {
  return kind === 'percent' ? Math.round(target * 1000) / 10 : target;
}
